import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

// --- DTOs ---
export interface SessionDto {
  id: number;
  name: string;
  startDate: string;
  endDate: string;
  schoolId: number;
  createdAt: string;
  updatedAt: string;
  active: boolean;
}

export interface SchoolClassDto {
  id: number;
  name: string;
  schoolId: number;
  createdAt: string;
  updatedAt: string;
  section: string;
  session: SessionDto;
}

export interface StudentDto {
  id: number;
  applicationNo: string;
  rollNumber: string;
  enrolmentNo: string;
  firstName: string;
  lastName: string;
  fatherName: string;
  motherName: string;
  dateOfBirth: string;
  gender: string;
  medium: string;
  caste: string;
  subCaste: string;
  casteCertificate: string;
  religion: string;
  sssmid: string;
  scholarNo: string;
  penNo: string;
  apparIdNo: string;
  aadharNo: string;
  houseNo: string;
  street: string;
  landmark: string;
  area: string;
  village: string;
  postOffice: string;
  district: string;
  state: string;
  pinCode: string;
  phone: string;
  email: string;
  bankName: string;
  ifcCode: string;
  accountNo: string;
  annualIncome: string;
  className: string;
  section: string;
  vocationalSubject1: string;
  vocationalSubject2: string;
  vocationalSubject3: string;
  mathMusicPaintingComputer: string;
  scienceMusicPaintingComputer: string;
  socialScience: string;
  admissionDate: string;
  admissionClass: string;
  schoolId: number;
  schoolClass: SchoolClassDto;
  guardianName: string;
  guardianPhone: string;
  guardianEmail: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  subjects: string;
  gmail: string;
  session: SessionDto;
  password: string;
  userId: number;
}

export interface StudentClassMappingDto {
  id: number;
  student: StudentDto;
  classId: number;
  session: SessionDto;
  rollNumber: number;
  joinedDate: string;
  leftDate: string;
  current: boolean;
}

const API_URL = `${process.env.REACT_APP_API_URL || ""}/student-class-mappings`;

// --- Queries ---
const getCurrentClass = async (
  studentId: number
): Promise<StudentClassMappingDto> => {
  const { data } = await axios.get(`${API_URL}/current/${studentId}`);
  return data;
};

const getClassHistory = async (
  studentId: number
): Promise<StudentClassMappingDto[]> => {
  const { data } = await axios.get(`${API_URL}/history/${studentId}`);
  return data;
};

const getStudentsInClassForSession = async (
  classId: number,
  sessionId: number
): Promise<StudentDto[]> => {
  const { data } = await axios.get(
    `${API_URL}/class/${classId}/session/${sessionId}`
  );
  return data;
};

// --- Mutations ---
const promoteStudent = async ({
  studentId,
  newClassId,
  sessionId,
  marksheetType,
  schoolType,
  withSubjects,
}: {
  studentId: number;
  newClassId: number;
  sessionId: number;
  marksheetType: string;
  schoolType: string;
  withSubjects?: boolean;
}): Promise<StudentDto> => {
  const STUDENTS_API_URL = `${process.env.REACT_APP_API_URL || ""}/students`;
  const { data } = await axios.post(
    `${STUDENTS_API_URL}/promote?studentId=${studentId}&newClassId=${newClassId}&sessionId=${sessionId}&marksheetType=${marksheetType}&schoolType=${schoolType}&withSubjects=${withSubjects ?? false}`
  );
  return data;
};

export function useStudentClassMapping() {
  const queryClient = useQueryClient();

  // Queries
  const useCurrentClass = (studentId?: number) =>
    useQuery<StudentClassMappingDto, Error>(
      ["studentClassMapping", "current", studentId],
      () => getCurrentClass(studentId!),
      { enabled: !!studentId }
    );

  const useClassHistory = (studentId?: number) =>
    useQuery<StudentClassMappingDto[], Error>(
      ["studentClassMapping", "history", studentId],
      () => getClassHistory(studentId!),
      { enabled: !!studentId }
    );

  const useStudentsInClassForSession = (classId?: number, sessionId?: number) =>
    useQuery<StudentDto[], Error>(
      ["studentClassMapping", "class", classId, "session", sessionId],
      () => getStudentsInClassForSession(classId!, sessionId!),
      { enabled: !!classId && !!sessionId }
    );

  // Mutations
  const promoteStudentMutation = useMutation({
    mutationFn: promoteStudent,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["studentClassMapping"] });
      queryClient.invalidateQueries({ queryKey: ["tclist"] }); // This will invalidate all tclist queries
      queryClient.invalidateQueries({ queryKey: ["students"] });
      queryClient.invalidateQueries({ queryKey: ["meritlist"] }); // Also invalidate meritlist queries
    },
  });

  return {
    useCurrentClass,
    useClassHistory,
    useStudentsInClassForSession,
    promoteStudentMutation,
  };
}
